import React from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ShieldCheck } from "lucide-react";

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-4 md:px-8 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center">
          <img src="/logo.svg?v=20" alt="Synappsi" className="h-10 w-auto" />
        </Link>
        <Link to="/login">
          <Button variant="outline" size="sm" data-testid="privacy-back">
            <ArrowLeft className="h-4 w-4 mr-1" /> Torna al login
          </Button>
        </Link>
      </header>

      <div className="max-w-3xl mx-auto p-4 md:p-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-lg bg-[#DBEAFE] text-[#1E3A8A] flex items-center justify-center">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Trasparenza</p>
            <h1 className="font-heading text-3xl font-extrabold text-slate-900">Informativa Privacy</h1>
          </div>
        </div>
        <p className="text-sm text-slate-500 mb-6">
          Ai sensi degli artt. 13 e 14 del Regolamento UE 2016/679 (GDPR) e del D.Lgs. 196/2003.
          Versione 1.0 — ultimo aggiornamento: febbraio 2026.
        </p>

        <Card className="p-6 bg-white border-slate-200 rounded-xl shadow-sm space-y-6 text-sm text-slate-700 leading-relaxed">
          <section>
            <h2 className="font-heading text-lg font-bold text-slate-900 mb-2">1. Titolare del trattamento</h2>
            <p>Il titolare del trattamento è il professionista/struttura che gestisce l'istanza di Synappsi.
              Ogni utente registrato agisce come titolare autonomo o contitolare per le comunicazioni
              cliniche che genera all'interno della piattaforma.</p>
          </section>
          <section>
            <h2 className="font-heading text-lg font-bold text-slate-900 mb-2">2. Categorie di dati</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Dati identificativi professionali</strong>: nome, email, ruolo, struttura, specialità, telefono.</li>
              <li><strong>Dati di autenticazione</strong>: password (memorizzata solo come hash bcrypt), cookie di sessione httpOnly.</li>
              <li><strong>Dati sanitari (Art. 9 GDPR)</strong>: nome/riferimento paziente e contenuto dei messaggi clinici scambiati.</li>
              <li><strong>Metadati</strong>: log accessi, indirizzo IP, timestamp, consenso privacy.</li>
            </ul>
          </section>
          <section>
            <h2 className="font-heading text-lg font-bold text-slate-900 mb-2">3. Finalità e base giuridica</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>Consentire la comunicazione clinica sicura tra professionisti sanitari (art. 9, § 2, lett. h — finalità di cura).</li>
              <li>Adempiere obblighi di tracciabilità (art. 6, § 1, lett. c).</li>
              <li>Consenso esplicito dell'utente professionista (art. 6, § 1, lett. a).</li>
            </ul>
          </section>
          <section>
            <h2 className="font-heading text-lg font-bold text-slate-900 mb-2">4. Notifiche email e riservatezza</h2>
            <p>Le notifiche email <strong>non contengono mai dati identificativi del paziente né il contenuto
              del messaggio clinico</strong>. Comunicano esclusivamente l'esistenza di una nuova comunicazione,
              rimandando l'utente al login. I dati sanitari restano confinati all'interno della piattaforma.</p>
          </section>
          <section>
            <h2 className="font-heading text-lg font-bold text-slate-900 mb-2">5. Sicurezza (Art. 32 GDPR)</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>Password memorizzate con hash bcrypt (mai in chiaro).</li>
              <li>Autenticazione via cookie httpOnly Secure SameSite=None (protezione XSS).</li>
              <li>Lockout automatico dopo 5 tentativi falliti (protezione brute force).</li>
              <li>Log accessi a ogni conversazione clinica (tracciabilità).</li>
              <li>Trasporto cifrato TLS end-to-end.</li>
            </ul>
          </section>
          <section>
            <h2 className="font-heading text-lg font-bold text-slate-900 mb-2">6. Conservazione</h2>
            <p>I dati vengono conservati per il periodo strettamente necessario alle finalità di cura e agli
              obblighi documentali di legge. L'utente può richiedere in ogni momento cancellazione o esportazione.</p>
          </section>
          <section>
            <h2 className="font-heading text-lg font-bold text-slate-900 mb-2">7. Diritti dell'interessato</h2>
            <p>L'utente può esercitare in ogni momento i diritti previsti dagli artt. 15–22 GDPR (accesso,
              rettifica, cancellazione, limitazione, portabilità, opposizione) contattando il titolare tramite
              email amministrativa.</p>
          </section>
          <section>
            <h2 className="font-heading text-lg font-bold text-slate-900 mb-2">8. Responsabilità dell'utente</h2>
            <p>L'utente professionista è responsabile del corretto inserimento dei dati sanitari, del rispetto
              del segreto professionale e del principio di minimizzazione: <strong>inserire solo le informazioni
              strettamente necessarie</strong> per la comunicazione clinica (preferendo, ove possibile, codici o
              pseudonimi al posto di nomi completi).</p>
          </section>
        </Card>
      </div>
    </div>
  );
}
